/* «Слайды с преимуществами» (infog, APP-SPEC-5 §13) и «Карусель для поста» (post, §14) — одна форма слайдов.
   Каркас §1.1: шапка → лоток фото → главное поле → лента «Оформление» → карточка «Ещё настройки» → кнопка; хвост — ProductOrderResult.
   Каждый контрол спецификации включается по наличию своего поля в опубликованной форме (product-model.ts KEYS);
   в заказ уходят только ключи формы. Неизвестное обязательное поле делает форму недоступной, а не молча теряется. */
import { useState } from 'react';
import type { UserAvatar } from '../../api/people';
import { uiFormat, uiText } from '../../i18n';
import { ChoiceTiles } from '../../ui/ChoiceTiles';
import { TextEditor } from '../../ui/TextEditor';
import { TextField } from '../../ui/TextField';
import { StudioCreateButton, StudioInfoRow, StudioSettings, StudioSettingsSection, type StudioSettingSlot } from '../studio/StudioFormParts';
import { ToolHeader } from '../studio/circle/ToolHeader';
import { toolPrice, unitText, useTailRetry } from '../studio/circle/tool-helpers';
import { StudioPhotoTray } from '../studio/StudioPhotoTray';
import { studioCheapOffer, studioExpectedPrice, type StudioApp } from '../studio/studio-api';
import { useStudioForm } from '../studio/use-studio-form';
import { useStudioPhotoPreviews } from '../studio/use-studio-photo-previews';
import {
  andList,
  count,
  enumOf,
  formatSpecs,
  INFOG_STYLES,
  INFOG_THEMES,
  joinProductText,
  label,
  parseSpecs,
  photoSlots,
  platformCell,
  PLATFORMS,
  POST_CHIPS,
  POST_FORMATS,
  POST_STYLES,
  POST_THEMES,
  POST_TONES,
  postStyleKey,
  ROW_FORMS,
  SLIDE_FORMS,
  SPEC_KEYS,
  splitProductText,
  stopWords,
  textOf,
  utf16,
  type SpecRow,
} from './product-model';
import { ProductExtractSheet } from './ProductExtractSheet';
import { ProductOrderResult } from './ProductOrderResult';
import { ProductVariantRail } from './ProductVariantRail';
import { slideContractSupported, type SlideKind } from './slide-model';
import { useProductDefaults } from './use-product-defaults';
import '../studio/native-template.css';
import '../post/post.css';
import '../infog/infog.css';
import './product.css';

export interface SlideOrderProps {
  app: StudioApp;
  avatar: UserAvatar | null;
  initialInputs?: Record<string, unknown>;
  headerActive?: boolean;
}
const COVER: Record<string, string> = { false: 'С надписями', true: 'Без надписей', text: 'С надписями', clean: 'Без надписей' };
const cleanValue = (values: string[]) => values.find(value => value === 'true' || value === 'clean');

export function SlideOrderForm({ kind, app, avatar, initialInputs, headerActive = true }: SlideOrderProps & { kind: SlideKind }) {
  const state = useStudioForm({ app, avatar, initialInputs });
  const { form, inputs, locked, orderId } = state;
  const supported = !!form && slideContractSupported(form, kind);
  const fields = {
    text: kind === 'post' ? textOf(form, 'topic') : textOf(form, 'text'),
    name: kind === 'infog' && !textOf(form, 'text') ? textOf(form, 'name') : undefined,
    advantages: kind === 'infog' && !textOf(form, 'text') ? textOf(form, 'advantages') : undefined,
    style: enumOf(form, 'style'),
    platform: kind === 'infog' ? enumOf(form, 'platform') : undefined,
    slides: enumOf(form, 'slides'),
    cover: kind === 'infog' ? enumOf(form, 'cover') : undefined,
    specs: kind === 'infog' ? textOf(form, 'specs') : undefined,
    format: kind === 'post' ? enumOf(form, 'format') : undefined,
    tone: kind === 'post' ? enumOf(form, 'tone') : undefined,
  };
  useProductDefaults(state, [
    ['platform', ['wb']],
    ['slides', kind === 'post' ? ['6'] : ['3']],
    ['format', ['4:5']],
    ['tone', ['friendly']],
    ['style', []],
    ['cover', ['false', 'text']],
  ]);
  const slots = supported ? photoSlots(form) : [];
  const previews = useStudioPhotoPreviews(state, slots);
  const [showPhotos, setShowPhotos] = useState(false);
  const [editor, setEditor] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);
  const set = (key: string | undefined, value: string) => {
    if (key) state.setInputs(previous => ({ ...previous, [key]: value }));
  };
  /* Главное поле: одно поле «Название и преимущества» или пара name + advantages формы. */
  const split = !!fields.name;
  const mainText = split
    ? (draft ?? joinProductText(inputs[fields.name!.key] ?? '', (fields.advantages ? (inputs[fields.advantages.key] ?? '') : '').split('\n')))
    : (inputs[fields.text?.key ?? ''] ?? '');
  const limit = split ? 60 + 6 * 41 : (fields.text?.max_length ?? (kind === 'post' ? 500 : 2000));
  const setMain = (value: string) => {
    if (!split) return set(fields.text?.key, value);
    setDraft(value);
    const parts = splitProductText(value);
    state.setInputs(previous => ({
      ...previous,
      [fields.name!.key]: parts.name,
      ...(fields.advantages ? { [fields.advantages.key]: parts.advantages.join('\n') } : {}),
    }));
  };
  const product = splitProductText(mainText);
  const platform = fields.platform ? inputs[fields.platform.key] : undefined;
  const fresh = platform === 'fresh';
  const clean = fields.cover ? cleanValue(fields.cover.values) : undefined;
  const specs = parseSpecs(fields.specs ? inputs[fields.specs.key] : '');
  const setSpecs = (next: SpecRow[]) => set(fields.specs?.key, formatSpecs(next));
  const uploaded = slots.filter(slot => inputs[slot.key]).length;
  const mainPhoto = slots[0] ? previews.urls[slots[0].key] : undefined;
  const full = kind === 'infog' && slots.length > 0;
  const warnings = kind === 'infog' ? stopWords([product.name, ...product.advantages, ...specs.map(([key, value]) => `${key} ${value}`)], platform) : null;
  const slidesCount = fields.slides ? inputs[fields.slides.key] : undefined;
  const missing = !form
    ? 'Проверяем доступность'
    : !supported
      ? 'Этот вариант пока недоступен'
      : slots.some(slot => slot.required) && !state.rights
        ? 'Подтвердите права на фото'
        : slots.some(slot => slot.required && !inputs[slot.key])
          ? 'Загрузите фото товара'
          : utf16(mainText) > limit
            ? 'Сократите текст'
            : kind === 'post'
              ? !uploaded && Array.from(mainText.trim()).length < 10
                ? slots.length
                  ? 'Добавьте тему от 10 знаков или свои фото'
                  : 'Добавьте тему поста, от 10 знаков'
                : null
              : (fields.text?.required || fields.name?.required) && !mainText.trim()
                ? 'Добавьте название и преимущества'
                : full && Array.from(product.name).length < 2
                  ? 'Добавьте название, от 2 знаков'
                  : full && Array.from(product.name).length > 60
                    ? 'Сократите название до 60 знаков'
                    : full && product.advantages.length < 3
                      ? 'Добавьте три преимущества'
                      : full && product.advantages.length > 6
                        ? 'Оставьте до 6 преимуществ'
                        : null;
  const create = () => {
    if (!state.busy && !state.submissionBlocked && (state.intent || (supported && form?.available && !missing))) void state.submit();
  };
  const styleDict = kind === 'post' ? POST_STYLES : INFOG_STYLES;
  const styleVariants = (fields.style?.values ?? []).map(value => ({ value, choice: styleDict[value] ?? { name: value } }));
  const ratio = kind === 'post' ? (inputs[fields.format?.key ?? ''] === '1:1' ? 'sq' : 'p45') : fresh ? 'sq' : 'p34';
  const preview = (style: string) =>
    kind === 'infog' ? (
      <span className={`px-ig px-ig-${style} ${ratio}`}>
        {mainPhoto ? <img src={mainPhoto} alt="" /> : <span className="px-ig-ph" aria-hidden="true" />}
        <b>{product.name || uiText('Ваш товар')}</b>
      </span>
    ) : (
      <span className={`px-po px-po-${postStyleKey(style)} ${ratio}`}>
        {mainPhoto && ['mag', 'photo', 'quote'].includes(postStyleKey(style)) && <img src={mainPhoto} alt="" />}
        <b>{mainText.trim().split('\n')[0] || uiText('О чём пост?')}</b>
      </span>
    );
  /* Ячейки карточки «Ещё настройки» в общем порядке секций (§1.5): формат, количество, площадка, остальное, файлы. */
  const cells: StudioSettingSlot[] = [
    ...(fields.format
      ? [{ title: 'Формат', value: label(POST_FORMATS, inputs[fields.format.key]) || uiText('Пост'), section: 'format', kind: 'choice' as const }]
      : []),
    ...(fields.slides && slidesCount ? [{ title: 'Сколько слайдов', value: count(slidesCount, SLIDE_FORMS), section: 'slides' }] : []),
    ...(fields.platform ? [{ title: 'Площадка', value: platformCell(platform), section: 'platform' }] : []),
    ...(fields.cover
      ? [
          fresh && clean
            ? {
                title: 'Обложка',
                value: COVER[clean] ?? clean,
                kind: 'info' as const,
                lock: true,
                wide: true,
                tip: 'У Ozon Fresh главное фото только без надписей',
              }
            : { title: 'Обложка', value: COVER[inputs[fields.cover.key] ?? ''] ?? (inputs[fields.cover.key] || ''), section: 'cover' },
        ]
      : []),
    ...(fields.specs
      ? [{ title: 'Характеристики', value: count(specs.length, ROW_FORMS), empty: !specs.length, wide: fresh, section: 'specs' }]
      : []),
    ...(fields.tone ? [{ title: 'Тон', value: label(POST_TONES, inputs[fields.tone.key]), section: 'tone' }] : []),
    ...(kind === 'post' && slots.length
      ? [
          {
            title: 'Свои фото',
            value: count(uploaded, ['фото', 'фото', 'фото']),
            empty: !uploaded,
            kind: 'files' as const,
            thumb: uploaded && mainPhoto ? <img src={mainPhoto} alt="" /> : undefined,
            onTap: () => setShowPhotos(true),
          },
        ]
      : []),
  ];
  const price = form ? studioExpectedPrice(form, inputs) : 0;
  const createLabel = kind === 'post' ? 'Создать карусель' : slidesCount ? `Создать ${count(slidesCount, SLIDE_FORMS)}` : 'Создать слайды';
  const unitPrice = toolPrice(app.price);
  const unit = unitText(unitPrice.amount, kind === 'post' ? 'C за карусель' : 'C за слайд');
  const retry = useTailRetry(state);
  const photoTray = slots.length > 0 && (kind === 'infog' || showPhotos || uploaded > 0) && (
    <>
      <StudioPhotoTray
        state={state}
        slots={slots}
        urls={previews.urls}
        minimum={slots.filter(slot => slot.required).length || (kind === 'post' ? 0 : 1)}
        title={kind === 'post' ? 'Свои фото' : 'Фото товара'}
        description={kind === 'post' ? 'Свои фото встанут на слайды' : 'Фон уберём сами'}
        consent={
          <label className="photo-consent">
            <input type="checkbox" checked={state.rights} disabled={locked} onChange={event => state.setRights(event.target.checked)} />
            {uiText(kind === 'post' ? 'Подтверждаю право использовать загружаемые фото' : 'Подтверждаю право использовать фото товара')}
          </label>
        }
      />
      {kind === 'infog' && <p className="hint px-cap1">{uiText('Главное фото пойдёт на обложку')}</p>}
      {previews.error && (
        <p role="alert">
          {uiText('Не удалось загрузить превью.')}{' '}
          <button type="button" onClick={previews.retry}>
            {uiText('Повторить')}
          </button>
        </p>
      )}
    </>
  );
  return (
    <section className={`native-studio-form ap-in server-${kind} product-form`}>
      <ToolHeader title={app.name} unit={unit} enabled={headerActive} />
      {!form && !state.error && <p role="status">{uiText('Загружаем…')}</p>}
      {state.error && (
        <p role="alert">
          {uiText(state.error)}{' '}
          <button type="button" disabled={state.busy} onClick={() => state.setRevision(value => value + 1)}>
            {uiText('Повторить')}
          </button>
        </p>
      )}
      {form && !orderId && (
        <form
          className="pf-body"
          onSubmit={event => {
            event.preventDefault();
            create();
          }}
        >
          {!supported && <p role="status">{uiText('Этот вариант пока недоступен')}</p>}
          {kind === 'infog' && photoTray}
          {supported && (fields.text || fields.name) && (
            <TextField
              label={uiText(kind === 'post' ? 'О чём пост?' : 'Название и преимущества')}
              value={mainText}
              limit={limit}
              minimum={kind === 'post' ? 10 : 2}
              disabled={locked}
              placeholder={uiText(kind === 'post' ? 'Тема или готовый текст' : 'Первая строка — название, дальше по преимуществу на строку')}
              onChange={setMain}
              onExpand={() => setEditor(true)}
              expandLabel={uiText('Во весь экран')}
            >
              {kind === 'post' && (
                <div className="chips px-chips">
                  {POST_CHIPS.map(chip => (
                    <button
                      key={chip}
                      type="button"
                      className={`chip${mainText === chip ? ' on' : ''}`}
                      disabled={locked}
                      onClick={() => setMain(mainText === chip ? '' : chip)}
                    >
                      {uiText(chip)}
                    </button>
                  ))}
                </div>
              )}
            </TextField>
          )}
          {supported && kind === 'post' && (
            <p className="hint">{uiText(uploaded ? 'Можно без темы: текст по фото' : 'Можно вставить готовый текст')}</p>
          )}
          {supported && kind === 'infog' && (
            <ProductExtractSheet
              kind="infog"
              disabled={locked}
              onFill={found => {
                setMain(joinProductText(found.name || product.name, [...product.advantages, ...found.advantages].slice(0, 6)));
                if (fields.specs && found.specs.length) setSpecs([...specs, ...found.specs].slice(0, 8));
              }}
            />
          )}
          {warnings && warnings.words.length > 0 && (
            <p className="note warn">
              {uiFormat(
                'Уберите {0}: {1} не принимает на фото {2}',
                andList(warnings.words, 3, true),
                label(PLATFORMS, platform) || uiText('площадка'),
                andList(warnings.groups, 3),
              )}
            </p>
          )}
          {supported && kind === 'post' && (
            <div className={`px-live ${ratio}`} aria-label={uiText('Предпросмотр')}>
              <span className={`px-po px-po-${postStyleKey(inputs[fields.style?.key ?? ''])} ${ratio} on`}>
                {mainPhoto && <img src={mainPhoto} alt="" />}
                <b>{mainText.trim().split('\n')[0] || uiText('О чём пост?')}</b>
                <i className="mono">1</i>
              </span>
              {[2, 3].map(n => (
                <span key={n} className={`px-po px-po-${postStyleKey(inputs[fields.style?.key ?? ''])} ${ratio} ghost`} aria-hidden="true">
                  <span className="px-sk" />
                  <span className="px-sk short" />
                  <i className="mono">{n}</i>
                </span>
              ))}
            </div>
          )}
          {supported && fields.style && (
            <ProductVariantRail
              title="Оформление"
              allTitle={kind === 'post' ? 'Все стили' : 'Все оформления'}
              variants={styleVariants}
              value={inputs[fields.style.key] ?? ''}
              disabled={locked}
              onChange={value => set(fields.style?.key, value)}
              preview={variant => preview(variant.value)}
              themes={kind === 'post' ? POST_THEMES : INFOG_THEMES}
              aboutLines={variant =>
                kind === 'infog'
                  ? [
                      variant.choice.themes?.length ? uiFormat('Подходит для: {0}', variant.choice.themes.join(', ')) : '',
                      'Текст на слайдах · Набираем шрифтом, без ошибок нейросети',
                    ].filter(Boolean)
                  : ['Текст на слайдах набирает шаблон STEOS']
              }
            />
          )}
          {kind === 'post' && photoTray}
          {supported && cells.length > 0 && (
            <StudioSettings disabled={locked} slots={cells} oneRow>
              {fields.format && (
                <StudioSettingsSection id="format" title="Формат">
                  <ChoiceTiles
                    label={uiText('Формат')}
                    variant="rows"
                    value={inputs[fields.format.key] ?? ''}
                    disabled={locked}
                    onChange={value => set(fields.format?.key, value)}
                    choices={fields.format.values.map(value => ({
                      value,
                      label: uiText(label(POST_FORMATS, value)),
                      description: POST_FORMATS[value]?.sub ? uiText(POST_FORMATS[value].sub!) : undefined,
                    }))}
                  />
                </StudioSettingsSection>
              )}
              {fields.slides && (
                <StudioSettingsSection id="slides" title="Сколько слайдов" kind="steps" hint={unit && kind === 'post' ? unit : undefined}>
                  <ChoiceTiles
                    label={uiText('Сколько слайдов')}
                    value={slidesCount ?? ''}
                    disabled={locked}
                    onChange={value => set(fields.slides?.key, value)}
                    choices={fields.slides.values.map(value => ({ value, label: value }))}
                  />
                </StudioSettingsSection>
              )}
              {fields.platform && (
                <StudioSettingsSection id="platform" title="Площадка">
                  <ChoiceTiles
                    label={uiText('Площадка')}
                    variant="rows"
                    value={platform ?? ''}
                    disabled={locked}
                    onChange={value => {
                      set(fields.platform?.key, value);
                      if (value === 'fresh' && fields.cover && clean) set(fields.cover.key, clean);
                    }}
                    choices={fields.platform.values.map(value => ({
                      value,
                      label: uiText(label(PLATFORMS, value)),
                      description: PLATFORMS[value]?.size,
                    }))}
                  />
                </StudioSettingsSection>
              )}
              {fields.cover && (
                <StudioSettingsSection id="cover" title="Обложка" kind={fresh && clean ? 'info' : 'choice'}>
                  {fresh && clean ? (
                    <StudioInfoRow title="Обложка" value={COVER[clean] ?? clean} tip="У Ozon Fresh главное фото только без надписей" lock />
                  ) : (
                    <ChoiceTiles
                      label={uiText('Обложка')}
                      value={inputs[fields.cover.key] ?? ''}
                      disabled={locked}
                      onChange={value => set(fields.cover?.key, value)}
                      choices={fields.cover.values.map(value => ({ value, label: uiText(COVER[value] ?? value) }))}
                    />
                  )}
                </StudioSettingsSection>
              )}
              {fields.specs && (
                <StudioSettingsSection id="specs" title="Характеристики" kind="custom" hint="до 8 строк">
                  <div className="px-specs" role="group" aria-label={uiText('Характеристики')}>
                    {specs.map(([key, value], index) => (
                      <div className="px-spec" key={index}>
                        <input
                          aria-label={uiFormat('Ключ {0}', index + 1)}
                          value={key}
                          maxLength={24}
                          disabled={locked}
                          onChange={event => setSpecs(specs.map((row, at) => (at === index ? [event.target.value, row[1]] : row)))}
                        />
                        <input
                          aria-label={uiFormat('Значение {0}', index + 1)}
                          value={value}
                          maxLength={24}
                          disabled={locked}
                          onChange={event => setSpecs(specs.map((row, at) => (at === index ? [row[0], event.target.value] : row)))}
                        />
                        <button
                          type="button"
                          aria-label={uiFormat('Убрать строку {0}', index + 1)}
                          disabled={locked}
                          onClick={() => setSpecs(specs.filter((_, at) => at !== index))}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    <button type="button" className="lnk" disabled={locked || specs.length >= 8} onClick={() => setSpecs([...specs, ['', '']])}>
                      {uiText('Добавить строку')}
                    </button>
                    <div className="chips">
                      {SPEC_KEYS.map(key => (
                        <button
                          key={key}
                          type="button"
                          className="chip"
                          disabled={locked || specs.length >= 8 || specs.some(([name]) => name === key)}
                          onClick={() => setSpecs([...specs, [key, '']])}
                        >
                          {uiText(key)}
                        </button>
                      ))}
                    </div>
                    <p className="hint">{uiText('Размер и вес дадут слайд «Размеры»')}</p>
                  </div>
                </StudioSettingsSection>
              )}
              {fields.tone && (
                <StudioSettingsSection id="tone" title="Тон">
                  <ChoiceTiles
                    label={uiText('Тон')}
                    variant="rows"
                    value={inputs[fields.tone.key] ?? ''}
                    disabled={locked}
                    onChange={value => set(fields.tone?.key, value)}
                    choices={fields.tone.values.map(value => ({
                      value,
                      label: uiText(label(POST_TONES, value)),
                      description: POST_TONES[value]?.sub ? uiText(POST_TONES[value].sub!) : undefined,
                    }))}
                  />
                </StudioSettingsSection>
              )}
              {kind === 'post' && slots.length > 0 && (
                <StudioSettingsSection id="photos" title="Свои фото" kind="files">
                  <button type="button" className="sc-r px-files-row" disabled={locked} onClick={() => setShowPhotos(true)}>
                    <span className="m">
                      <b>{uiText('Свои фото')}</b>
                      <small>{uiText(uploaded ? count(uploaded, ['фото', 'фото', 'фото']) : 'Рамка своих фото встанет на форму')}</small>
                    </span>
                    <span aria-hidden="true">{uiText('Добавить')} ›</span>
                  </button>
                </StudioSettingsSection>
              )}
            </StudioSettings>
          )}
          {form.chargeable === false && <p>{uiText('Без списания')}</p>}
          <StudioCreateButton
            kind="photo"
            label={createLabel}
            price={price}
            busy={state.busy}
            blocked={state.submissionBlocked}
            pending={!!state.intent}
            missing={missing ? uiText(missing) : null}
            available={form.available && supported}
            balance={state.balance}
            cheap={studioCheapOffer(form)}
            onCheap={() => void state.submit({ cheap: true })}
            onCreate={create}
          />
        </form>
      )}
      {editor && (
        <TextEditor
          label={uiText(kind === 'post' ? 'О чём пост?' : 'Название и преимущества')}
          value={mainText}
          limit={limit}
          disabled={locked}
          onChange={setMain}
          onClose={() => setEditor(false)}
        />
      )}
      <ProductOrderResult
        state={state}
        kind={kind}
        hero={mainPhoto}
        onRetry={retry}
        keys={{
          text: fields.text?.key ?? fields.name?.key,
          platform: fields.platform?.key,
          style: fields.style?.key,
          format: fields.format?.key,
          tone: fields.tone?.key,
          photos: slots.map(slot => slot.key),
        }}
      />
    </section>
  );
}
